<template>
  <block-item-card
    ref="baselTestBlock"
    :title="data.scenario.baselTitle"
    tooltip="data.scenario.scenarioName"
    class="fill-height d-flex flex-column"
  >
    <template #header2>
      <v-card-title
        v-if="data.scenario.scenarioName"
        :class="subtitleClass"
        :style="subtitleStyle"
      >
        {{ data.scenario.scenarioName }}
      </v-card-title>
    </template>
    <template #actions>
      <v-spacer />
      <div class="d-flex flex-column mt-6 align-end">
        <div class="text-size-12">
          {{ $t('EVE.Section.BaselTests.Blocks.TotalEveChange') }}
        </div>
        <div :class="`text-size-24 font-weight-medium ${data.deltaEve >= 0 ? 'synthesis-ui-green-02--text' : 'synthesis-ui-red-02--text'}`">
          {{ formattedTotalEve }}
        </div>
      </div>
    </template>

    <v-lazy
      :options="{threshold: .5}"
      transition="fade-transition"
    >
      <e-chart
        :autoresize="true"
        :option="chartOpts"
        :style="`height: 400px;`"
        class="chart"
      />
    </v-lazy>
    <div class="eve-table mt-6">
      <div class="eve-table__head">
        <div class="col label">
          {{ $t('EVE.Section.BaselTests.Blocks.TableHeaders.PresentValue') }}
        </div>
        <div class="col legend">
          <span
            class="dot"
            :style="`background:${$vuetify.theme.themes.light['synthesis-ui-blue-04']}`"
          />
          <span>{{ $t('EVE.Section.BaselTests.Blocks.TableHeaders.Base') }}</span>
        </div>
        <div class="col legend">
          <span
            class="dot"
            :style="`background:${$vuetify.theme.themes.light['synthesis-ui-green-01']}`"
          />
          <span>{{ $t('EVE.Section.BaselTests.Blocks.TableHeaders.Shock') }}  </span>
        </div>
        <div class="col delta">
          {{ $t('EVE.Section.BaselTests.Blocks.TableHeaders.Delta') }}
        </div>
      </div>

      <div
        v-for="row in tableRows"
        :key="row.key"
        class="eve-row"
      >
        <div class="col label-item">
          {{ row.label }}
        </div>
        <div
          class="col value"
          :class="valueClass(row.base)"
        >
          {{ fmt(row.base) }}
        </div>
        <div
          class="col value"
          :class="valueClass(row.shock)"
        >
          {{ fmt(row.shock) }}
        </div>
        <div
          class="col value"
          :class="valueClass(row.delta)"
        >
          {{ fmt(row.delta) }}
        </div>
      </div>

      <div class="eve-total">
        <div class="col total-label">
          {{ $t('EVE.Section.BaselTests.Blocks.TableRows.TotalEve') }}
        </div>
        <div
          class="col total-value"
          :class="valueClass(totalBase)"
        >
          {{ fmt(totalBase) }}
        </div>
        <div
          class="col total-value"
          :class="valueClass(totalShock)"
        >
          {{ fmt(totalShock) }}
        </div>
        <div
          class="col total-delta"
          :class="valueClass(totalDelta)"
        >
          {{ fmt(totalDelta) }}
        </div>
      </div>
    </div>
  </block-item-card>
</template>
      <script>
import BlockItemCard      from '@/theme/default/components/common/BlockItemCard'
import chartsColorMixin   from '@/theme/default/components/charts/mixins/chartsColorMixin'
import { use }                 from 'echarts/core'
import { BarChart }            from 'echarts/charts'
import { UniversalTransition } from 'echarts/features'
import { CanvasRenderer }      from 'echarts/renderers'

import { GridComponent, LegendComponent, TitleComponent, TooltipComponent, TransformComponent } from 'echarts/components'
import {  toNumberFormatNotationShort } from '@/lib/utils/helper'

use([
  BarChart,
  GridComponent,
  LegendComponent,
  TitleComponent,
  TooltipComponent,
  TransformComponent,
  CanvasRenderer,
  UniversalTransition
])

export default {
  name      : 'BaselTestBlock',
  components: { BlockItemCard },
  mixins    : [chartsColorMixin],
  props     : {
    data: {
      type   : Object,
      default: () => null
    },
    baseItem: {
      type   : Object,
      default: () => null
    },
    maturities: {
      type   : Array,
      default: () => []
    },
    riskBaseCurrency: {
      type   : Object,
      default: () => null
    },
    color: {
      type   : String,
      default: 'synthesis-ui-blue-04'
    },
    subtitleColor: {
      type   : String,
      default: null
    },
    subtitleSize: {
      type   : Number,
      default: 24
    }
  },
  data () {
    return {}
  },
  computed: {
    arrowMarkPoints () {
      const base  = this.baseItem?.rates || []
      const shock = this.data?.rates || []
      const cats  = (this.maturities || []).map(m => (m || '').toLowerCase())
      const n = Math.min(base.length, shock.length, cats.length)
      if (!n) return []

      const d = new Array(n)
      for (let i = 0; i < n; i++) d[i] = Number(shock[i] ?? 0) - Number(base[i] ?? 0)

      // Build contiguous segments with the same sign (ignore zero-only runs)
      const segs = []
      let i = 0
      while (i < n) {
        // skip leading zeros; glue zeros to the nearest non-zero sign if possible
        const s = Math.sign(d[i])
        let j = i
        while (j + 1 < n) {
          const next = Math.sign(d[j + 1])
          // keep extending while sign stays same or next is 0
          if (next === s || next === 0) j++
          else break
        }
        if (s !== 0) segs.push({ start: i, end: j, sign: s }) // inclusive indices
        i = j + 1
      }

      const arrows = []
      for (const seg of segs) {
        const { start, end, sign } = seg
        if (end <= start) continue

        // area weights per interval (trapezoid height = |d|)
        const weights = []
        let total = 0
        for (let k = start; k < end; k++) {
          const w = (Math.abs(d[k]) + Math.abs(d[k + 1])) / 2
          weights.push(w); total += w
        }
        if (total <= 0) continue

        // find interval where cumulative area reaches 50%
        const target = total * 0.5
        let cum = 0; let kStar = start
        for (let t = 0; t < weights.length; t++) {
          cum += weights[t]
          if (cum >= target) { kStar = start + t; break }
        }

        // place at the middle of interval [kStar, kStar+1]
        const i0 = kStar
        const i1 = Math.min(kStar + 1, n - 1)

        // vertical midpoint between the two lines (linear mid)
        const yMid = (base[i0] + base[i1] + shock[i0] + shock[i1]) / 4

        // Use the right category so it visually centers; category axes are discrete,
        // so we anchor to the right bin and it lands near the interval center.
        const xCat = cats[i1]

        const up = sign > 0
        const color = this.themeColorRGBA('synthesis-ui-blue-05')

        arrows.push({
          coord       : [xCat, yMid],
          symbol      : 'path://M8.54144 0.808936L8.54144 8.98323L13.1419 4.38272L13.1419 8.7463L7.06058 14.8277L0.979223 8.7463L0.979223 4.38272L5.57973 8.98323L5.57974 0.808935L8.54144 0.808936Z',
          symbolRotate: up ? 180 : 0,
          symbolSize  : 25,
          itemStyle   : {
            color
          },
          label: { show: false }
        })
      }

      return arrows
    },
    tableRows () {
      if (!this.data) return []

      const rows = [
        {
          key  : 'fixedLoans',
          label: this.$t('EVE.Section.BaselTests.Blocks.TableRows.FixedLoans'),
          shock: this.data.eveFixedLoans,
          delta: this.data.deltaEveFixedLoans
        },
        {
          key  : 'floatingLoans',
          label: this.$t('EVE.Section.BaselTests.Blocks.TableRows.FloatingLoans'),
          shock: this.data.eveFloatingLoans,
          delta: this.data.deltaEveFloatingLoans
        },
        {
          key  : 'fixedDeposits',
          label: this.$t('EVE.Section.BaselTests.Blocks.TableRows.FixedDeposits'),
          shock: this.data.eveFixedDeposits,
          delta: this.data.deltaEveFixedDeposits
        },
        {
          key  : 'behavDeposits',
          label: this.$t('EVE.Section.BaselTests.Blocks.TableRows.BehavDeposits'),
          shock: this.data.eveFloatingDeposits,
          delta: this.data.deltaEveFloatingDeposits
        }
      ]

      if (this.data.eveFixedInvestments && this.data.deltaEveFixedInvestments) {
        rows.push({
          key  : 'fixedInvestments',
          label: this.$t('EVE.Section.BaselTests.Blocks.TableRows.FixedInvestments'),
          shock: this.data.eveFixedInvestments,
          delta: this.data.deltaEveFixedInvestments
        })
      }

      if (this.data.eveFloatingInvestments && this.data.deltaEveFloatingInvestments) {
        rows.push({
          key  : 'floatingInvestments',
          label: this.$t('EVE.Section.BaselTests.Blocks.TableRows.FloatingInvestments'),
          shock: this.data.eveFloatingInvestments,
          delta: this.data.deltaEveFloatingInvestments
        })
      }

      if (this.data.eveFixedSwapAssets && this.data.deltaEveFixedSwapAssets) {
        rows.push({
          key  : 'fixedIrsAssets',
          label: this.$t('EVE.Section.BaselTests.Blocks.TableRows.FixedIrsAssets'),
          shock: this.data.eveFixedSwapAssets,
          delta: this.data.deltaEveFixedSwapAssets
        })
      }

      if (this.data.eveFloatingSwapAssets && this.data.deltaEveFloatingSwapAssets) {
        rows.push({
          key  : 'floatingIrsAssets',
          label: this.$t('EVE.Section.BaselTests.Blocks.TableRows.FloatingIrsAssets'),
          shock: this.data.eveFloatingSwapAssets,
          delta: this.data.deltaEveFloatingSwapAssets
        })
      }

      if (this.data.eveFixedSwapLiabilities && this.data.deltaEveFixedSwapLiabilities) {
        rows.push({
          key  : 'fixedIrsLiabilities',
          label: this.$t('EVE.Section.BaselTests.Blocks.TableRows.FixedIrsLiabilities'),
          shock: this.data.eveFixedSwapLiabilities,
          delta: this.data.deltaEveFixedSwapLiabilities
        })
      }

      if (this.data.eveFloatingSwapLiabilities && this.data.deltaEveFloatingSwapLiabilities) {
        rows.push({
          key  : 'floatingIrsLiabilities',
          label: this.$t('EVE.Section.BaselTests.Blocks.TableRows.FloatingIrsLiabilities'),
          shock: this.data.eveFloatingSwapLiabilities,
          delta: this.data.deltaEveFloatingSwapLiabilities
        })
      }

      rows.push({
        key  : 'cash',
        label: this.$t('EVE.Section.BaselTests.Blocks.TableRows.Cash'),
        shock: this.data.eveCash,
        delta: this.data.deltaEveCash
      })

      const res = rows
        .filter(r => r.shock != null && r.delta != null)
        .map(r => ({ ...r, base: Number(r.shock) - Number(r.delta) }))

      return res
    },

    // Totals
    totalShock () { return Number(this.data?.totalEve ?? 0) },
    totalDelta () { return Number(this.data?.deltaEve ?? 0) }, // Δ = Shock − Base
    totalBase  () { return this.totalShock - this.totalDelta }, // Base = Shock − Δ

    chartOpts () {
      if (!this.baseItem || !this.data) {
        return {}
      }

      // console.log(this.data)

      return {
        animation        : true,
        animationDuration: 1000,
        animationEasing  : 'quinticOut',
        tooltip          : {
          show           : true,
          trigger        : 'axis',
          backgroundColor: this.themeColorRGBA('synthesis-ui-grey-01', 0.9),
          borderColor    : this.themeColorRGBA('synthesis-ui-grey-01'),
          borderWidth    : 1,
          textStyle      : { color: '#FFFFFF' },
          formatter      : (params) => {
            if (!params[0].value || params[0].value === '-') {
              return null
            }

            return `
                          <div style="margin: 4px 0;">
                              <span style="display: inline-block; width: 12px; height: 12px; background: ${params[1].color}; margin-right: 8px;"></span>
                              <span>${params[1].axisValue}: ${params[1].value}%</span>
                              <br/>
                              <span style="display: inline-block; width: 12px; height: 12px; background: ${params[0].color}; margin-right: 8px;"></span>
                              <span>${params[0].axisValue}: ${params[0].value}%</span>
                              </div>
                          </div>`
          }
        },
        legend: { show: false },
        grid  : {
          left        : 30,
          top         : 20,
          right       : 10,
          bottom      : 10,
          containLabel: true
        },
        xAxis: {
          type     : 'category',
          data     : this.maturities.map(item => item.toLowerCase()),
          axisLabel: {
            margin  : 30,
            show    : true,
            color   : this.themeColorRGBA('synthesis-ui-grey-01'),
            fontSize: 12
          },
          axisLine : { show: false },
          axisTick : { show: false },
          splitLine: {
            show     : true,
            lineStyle: {
              color: this.themeColorRGBA('synthesis-ui-grey-06', 0.3),
              type : 'dashed'
            }
          }
        },
        yAxis: {
          type     : 'value',
          axisLabel: {
            margin       : -15,
            show         : true,
            color        : this.themeColorRGBA('synthesis-ui-grey-01'),
            fontSize     : 11,
            inside       : true,
            verticalAlign: 'bottom',
            formatter    : (value) => {
              return value + '%'
            }
          },
          axisLine : { show: false },
          axisTick : { show: false },
          splitLine: {
            show     : true,
            lineStyle: {
              color: this.themeColorRGBA('synthesis-ui-grey-02', 1),
              type : 'dashed',
              width: 2
            }
          }
        },
        series: [
          {
            name     : 'BaselTestItem Rates',
            type     : 'line',
            data     : this.data?.rates,
            itemStyle: {
              color: this.themeColorRGBA(this.color)
            },
            lineStyle: {
              width: 3
            },
            symbol    : 'emptyCircle',
            symbolSize: 10,
            smooth    : true
          },
          {
            name     : 'BaseItem Rates',
            type     : 'line',
            data     : this.baseItem?.rates,
            itemStyle: {
              color: this.themeColorRGBA('synthesis-ui-blue-04')
            },
            lineStyle: {
              width: 3
            },
            symbol    : 'emptyCircle',
            symbolSize: 10,
            smooth    : true
          },
          {
            name     : 'band-base',
            type     : 'line',
            stack    : 'band',
            data     : this.bandData?.lower,
            symbol   : 'none',
            lineStyle: { width: 0 }, // invisible
            areaStyle: null,
            z        : 1,
            silent   : true,
            tooltip  : { show: false }
          },
          {
            name     : 'band-blue',
            type     : 'line',
            stack    : 'band',
            data     : this.bandData?.diffBlue,
            symbol   : 'none',
            lineStyle: { width: 0 }, // invisible line, we only want the area
            areaStyle: {
              color: {
                type      : 'linear',
                x         : 0,
                y         : 0,
                x2        : 0,
                y2        : 1,
                colorStops: [
                  { offset: 0, color: this.themeColorRGBA('synthesis-ui-blue-08', 0.3) },
                  { offset: 1, color: this.themeColorRGBA(this.color, 0.3) }
                ]
              }
            },
            z      : 1,
            silent : true,
            tooltip: { show: false }
          },
          {
            name     : 'band-orange',
            type     : 'line',
            stack    : 'band',
            data     : this.bandData?.diffGreen,
            symbol   : 'none',
            lineStyle: { width: 0 }, // invisible line, area only
            areaStyle: {
              color: {
                type      : 'linear',
                x         : 0,
                y         : 0,
                x2        : 0,
                y2        : 1,
                colorStops: [
                  // use same subtle tint so the band looks unified
                  { offset: 0, color: this.themeColorRGBA('synthesis-ui-blue-08', 0.3) },
                  { offset: 1, color: this.themeColorRGBA(this.color, 0.03) }
                ]
              }
            },
            z      : 1,
            silent : true,
            tooltip: { show: false }
          },
          {
            name     : 'arrows',
            type     : 'line',
            data     : [],
            z        : 20,
            silent   : true,
            lineStyle: { width: 0 },
            markPoint: {
              symbolKeepAspect: true,
              animation       : true,
              data            : this.arrowMarkPoints
            },
            tooltip: { show: false }
          }

        ]
      }
    },
    bandData () {
      const a = this.data?.rates ?? []
      const b = this.baseItem?.rates ?? []
      const n = Math.min(a.length, b.length)
      const lower = new Array(n)
      const diffBlue = new Array(n) // blue above orange
      const diffGreen = new Array(n) // green above blue

      for (let i = 0; i < n; i++) {
        const A = Number(a[i] ?? 0)
        const B = Number(b[i] ?? 0)
        if (A >= B) {
          lower[i] = B
          diffBlue[i] = A - B
          diffGreen[i] = 0
        } else {
          lower[i] = A
          diffBlue[i] = 0
          diffGreen[i] = B - A
        }
      }
      return { lower, diffBlue, diffGreen }
    },
    formattedTotalEve () {
      const v = toNumberFormatNotationShort(Math.abs(this.data.deltaEve))
      const prefix = (this.data.deltaEve > 0 ? '' : '-') + this.riskBaseCurrency?.displaySymbol
      return prefix + v
    },
    subtitleClass () {
      const baseClass = 'pt-0 pl-4 font-weight-medium'
      const sizeClass = this.subtitleSize ? `text-size-${this.subtitleSize}` : 'text-size-24'
      const colorClass = this.subtitleColor ? `${this.subtitleColor}--text` : 'synthesis-brand-dark-blue--text'
      return `${baseClass} ${sizeClass} ${colorClass}`
    },
    subtitleStyle () {
      return 'margin-top: -5px'
    }
  },
  created () {
    // console.log(this.data, this.baseItem)
  },
  methods: {
    fmt (v) {
      if (v === 0) return '-'
      const n = Number(v) || 0
      const sign = n < 0 ? '-' : ''
      return `${sign}${this.riskBaseCurrency?.displaySymbol}${toNumberFormatNotationShort(Math.abs(n))}`
    },
    valueClass (v) {
      return (Number(v) || 0) < 0 ? 'neg' : 'pos'
    }
  }
}
    </script>
    <style scoped>
      .chart {
        width: 100%;
      }
      .eve-table {
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;
  margin:20px;
}

/* Header */
.eve-table__head {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 0.7fr;
  align-items: center;
  padding: 6px 10px;
  color: var(--v-synthesis-ui-grey-01-base);
  font-weight: 500;
  font-size: 12px;
}

.dot {
  width: 10px; height: 10px; border-radius: 50%;
  display: inline-block; margin-right: 6px;
}

/* Rows */
.eve-row {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 0.7fr;
  align-items: center;
  background: var(--v-synthesis-ui-grey-06-base);
  border-radius: 12px;
  padding: 0px 12px;
}

.col.label-item {
  font-size: 14px;
  font-weight: 400;
  color: var(--v-synthesis-ui-blue-01-base);
}

.col.value {
  text-align: center;
  font-size: 14px;
  font-weight: 500;
}

.pos { color: #0c2435; }                                  /* dark navy */
.neg { color: var(--v-synthesis-ui-red-02-base); }         /* red for negatives */

/* Footer total row */
.eve-total {
  margin-top: 6px;
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 0.7fr;
  align-items: center;
  border: 1px solid #D9E4F0;
  border-radius: 12px;
  padding: 0px 12px;
  background: #fff;
}

.total-label {
  font-size: 14px;
  font-weight: 700;
  color: #4f6673;
}

.total-value, .total-delta {
  font-size: 14px;
  font-weight: 700;
  text-align: center;
}

/* Small legend columns in header */
.eve-table .legend {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;

}

.delta {
  text-align: center;
}

    </style>
